/**
 * Money and ratio primitives.
 *
 * Every authoritative amount in PTS Compass is an integer number of minor units
 * (HKD cents). Binary floating point never touches a figure that reaches a client.
 * Arithmetic that needs division or percentages goes through decimal.js and comes
 * back as an integer, with the rounding rule stated at the call site.
 *
 * On PostgreSQL these columns become numeric(18,2); the conversion is mechanical
 * because nothing outside this file knows how a Money is stored.
 */
import { Decimal } from 'decimal.js';
// 28 significant digits is far more than HKD amounts need and keeps intermediate
// products (rate x hours x three multipliers x uplift) exact.
Decimal.set({ precision: 28, toExpNeg: -9e15, toExpPos: 9e15 });
export const MINOR_UNITS = 100;
export function money(minorUnits) {
    if (!Number.isInteger(minorUnits)) {
        throw new TypeError(`Money must be an integer number of minor units, received ${minorUnits}`);
    }
    if (!Number.isSafeInteger(minorUnits)) {
        throw new RangeError(`Money out of safe integer range: ${minorUnits}`);
    }
    return minorUnits;
}
export const ZERO = money(0);
/** Build a Money from a major-unit value, e.g. fromMajor(7750) -> HKD 7,750.00 */
export function fromMajor(major) {
    return money(new Decimal(major).times(MINOR_UNITS).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber());
}
export function toMajor(m) {
    return new Decimal(m).dividedBy(MINOR_UNITS).toNumber();
}
export function ratio(value) {
    const d = new Decimal(value);
    if (!d.isFinite())
        throw new TypeError(`Ratio must be finite, received ${String(value)}`);
    return d.toNumber();
}
export const RATIO_ZERO = ratio(0);
/**
 * A ratio expressed as a percentage in the interface is stored as a fraction.
 * This is the single place the two conventions meet — see normalisation N5.
 */
export function ratioFromPercent(percent) {
    return ratio(new Decimal(percent).dividedBy(100));
}
export function percentFromRatio(r) {
    return new Decimal(r).times(100).toDecimalPlaces(4).toNumber();
}
export function add(...amounts) {
    return money(amounts.reduce((total, a) => total + a, 0));
}
export function subtract(a, b) {
    return money(a - b);
}
export function negate(a) {
    return money(-a);
}
/** Multiply an amount by a plain quantity or factor, rounding half up to the cent. */
export function multiply(amount, factor) {
    return money(new Decimal(amount).times(factor).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber());
}
export function divide(amount, divisor) {
    const d = new Decimal(divisor);
    if (d.isZero())
        throw new RangeError('Division by zero in money arithmetic');
    return money(new Decimal(amount).dividedBy(d).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber());
}
export function applyRatio(amount, r) {
    return multiply(amount, r);
}
/**
 * Sell price from cost at a gross margin: cost / (1 - margin).
 * This is the PTS default for hardware, licences and subcontract.
 */
export function sellFromGrossMargin(cost, margin) {
    const m = new Decimal(margin);
    if (m.greaterThanOrEqualTo(1)) {
        throw new RangeError(`Gross margin must be below 1, received ${String(margin)}`);
    }
    return money(new Decimal(cost).dividedBy(new Decimal(1).minus(m)).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber());
}
/** Sell price from cost at a markup: cost * (1 + markup). */
export function sellFromMarkup(cost, markup) {
    return money(new Decimal(cost).times(new Decimal(1).plus(markup)).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber());
}
/** Realised gross margin on a line: (sell - cost) / sell. Null when sell is zero. */
export function marginOf(sell, cost) {
    if (sell === 0)
        return null;
    return ratio(new Decimal(sell).minus(cost).dividedBy(sell));
}
/**
 * Round up to the nearest multiple of `incrementMajor` major units.
 *
 * PTS rounds up, always — retainers to HKD 250, small works to HKD 100, projects
 * and audits to HKD 500, procurement unit prices to HKD 1. There is deliberately
 * no round-down helper in this module.
 */
export function roundUpTo(amount, incrementMajor) {
    if (incrementMajor <= 0)
        throw new RangeError('Rounding increment must be positive');
    const step = new Decimal(incrementMajor).times(MINOR_UNITS);
    return money(new Decimal(amount).dividedBy(step).ceil().times(step).toNumber());
}
/** Round a day or hour quantity up to the nearest step, e.g. half a day. */
export function roundUpQuantity(value, step) {
    const s = new Decimal(step);
    if (s.lessThanOrEqualTo(0))
        throw new RangeError('Quantity step must be positive');
    return new Decimal(value).dividedBy(s).ceil().times(s).toNumber();
}
export function maxMoney(...amounts) {
    return money(Math.max(...amounts));
}
export function isZero(a) {
    return a === 0;
}
/** Format for display and for documents. British English, HKD by default. */
export function formatMoney(amount, currency = 'HKD', withDecimals = false) {
    const major = new Decimal(amount).dividedBy(MINOR_UNITS);
    const body = major.toFixed(withDecimals ? 2 : 0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${currency} ${body}`;
}
export function formatPercent(r, dp = 1) {
    return `${percentFromRatio(r).toFixed(dp)}%`;
}
export { Decimal };
//# sourceMappingURL=money.js.map